import {
  AlignedPrograms,
  CONFIG,
  CostOfAttendanceSummary,
  EducationLevelEnum,
  GraduationRateInfo,
  GrantsAndScholarshipsSummary,
  Institution,
  InstructionalProgram,
  LivingConditionTypeEnum,
  Location,
  MedianYearsOfStudyInfo,
  Occupation,
  PercentileCareerDistribution,
  RefineEducationCost,
  ResidencyTypeEnum,
  RocCalculatorOutput,
  StartingYearDelayInfo
} from '@lib/utils';
import { RocModelProps } from '../../domain/roc-model/roc-model';

import { RocCalculatorInput } from '../interfaces';

export interface RocModelDto {
  rocModelId: string;
  defaultName: string;
  name: string;

  goalSalaryAnalysis: PercentileCareerDistribution;
  locationModel: Location;
  occupationModel: Occupation | null;
  degreeLevel: EducationLevelEnum;
  degreeProgram: InstructionalProgram;
  retirementAge: number;

  institutionModel: Institution;
  alignedPrograms: AlignedPrograms | null;
  startYear: number;
  startingYearDelay?: StartingYearDelayInfo;
  isFulltime: boolean;
  yearsToCompleteDegree: number | null;
  medianYearsOfStudyInfo?: MedianYearsOfStudyInfo;
  graduationRateInfo?: GraduationRateInfo;
  netPriceByYear: number[];

  costOfAttendanceSummary?: CostOfAttendanceSummary;
  grantsAndScholarshipsSummary?: GrantsAndScholarshipsSummary;
  residencyType: ResidencyTypeEnum;
  livingConditionTypeEnum: LivingConditionTypeEnum;

  isTaxDependent: boolean;
  prefersIncomeBasedRepayment: boolean;
  outOfPocketExpensesByYear: number[];
  federalSubsidizedLoanAmountByYear: number[];
  federalUnsubsidizedLoanAmountByYear: number[];
  federalLoanAmountByYear: number[];
  privateLoanAmountByYear: number[];
  yearsToPayOffFederalLoan: number;
  yearsToPayOffPrivateLoan: number;

  radiusInMiles: number;

  currentStateInput?: RocCalculatorInput;
  currentStateOutput?: RocCalculatorOutput;
  currentStateHash?: string;
  goalStateInput?: RocCalculatorInput;
  goalStateOutput?: RocCalculatorOutput;
  goalStateHash?: string;

  federalSubsidizedLoanLimitByYear: number[];
  totalFederalLoanLimitByYear: number[];
  federalSubsidizedLoanLimitAggregate: number;
  totalFederalLoanLimitAggregate: number;

  refineEducationCost?: RefineEducationCost;
  eligiblePellAmount?: number[];
  isEligibleForSubsidizedLoanByYear?: boolean[];
}

export class RocModelHelper {
  static get defaults(): RocModelProps {
    const props: RocModelProps = {
      defaultName: 'ROI Model',
      name: 'ROI Model',

      goalSalaryAnalysis: null,
      locationModel: null,
      occupationModel: null,
      degreeLevel: EducationLevelEnum.HighSchoolDiploma,
      degreeProgram: null,
      retirementAge: CONFIG.DEFAULTS.RETIREMENT_AGE,

      alignedPrograms: null,
      institutionModel: null,
      startYear: new Date().getFullYear(),
      isFulltime: true,
      yearsToCompleteDegree: CONFIG.DEFAULTS.YEARS_TO_COMPLETE,

      residencyType: ResidencyTypeEnum.IN_STATE,
      livingConditionTypeEnum: LivingConditionTypeEnum.ON_CAMPUS,

      isTaxDependent: true,
      prefersIncomeBasedRepayment: false,
      outOfPocketExpensesByYear: [],
      federalSubsidizedLoanAmountByYear: [0],
      federalUnsubsidizedLoanAmountByYear: [0],
      federalLoanAmountByYear: [0],
      privateLoanAmountByYear: [0],
      yearsToPayOffFederalLoan: CONFIG.DEFAULTS.PAY_OFF_FEDERAL_LOAN_IN_YEARS,
      yearsToPayOffPrivateLoan: CONFIG.DEFAULTS.PAY_OFF_PRIVATE_LOAN_IN_YEARS,

      radiusInMiles: CONFIG.DEFAULTS.SEARCH_RADIUS_IN_MILES,

      currentStateInput: null,
      currentStateOutput: null,
      currentStateHash: null,
      goalStateInput: null,
      goalStateOutput: null,
      goalStateHash: null,

      refineEducationCost: null,
      eligiblePellAmountByYear: null,
      isEligibleForSubsidizedLoanByYear: null
    };

    return props;
  }
}
